import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';

import { useFormatMessage } from '../../../../hooks';
import { IProduct } from '../../../../interfaces/product';
import { ICategoryItem } from '../../categories/interfaces';
import { actions as productActions } from '../../../../store/ducks/product.duck';
import { IUser } from '../../../../interfaces/user';
import { ITypeParameter } from '../../../../interfaces/productType';

const findCategory = (item: ICategoryItem, id: string, categories: any[]): any => {
  if (!item.children) return categories;
  const category = item.children?.find(i => i.id?.toString() === id);
  if (category) {
    return [...categories, category];
  }
  for (const i of item.children) {
    if (i.children) {
      const newCategories = findCategory(i, id, categories);
      if (newCategories && newCategories.length > 0)
        return [...categories, i, ...newCategories];
    }
  }
  return categories;
};

const getInitValues = (
  locale: string,
  product?: IProduct,
  productCompany?: { id?: number; name?: string },
  parentCategoryId?: string,
  categories?: ICategoryItem[],
  isFavorite?: boolean,
  catId?: string
) => {
  const initValues: { [key: string]: any } = {
    name: product?.name || '',
    // price: product?.price || '',
    active: product ? product?.active : true,
    mainCategoryId: parentCategoryId || product?.category?.id,
    categoryId_1: '',
    files: [],
    productTypeId: product?.product_type?.id,
    sku: product?.sku || '',
    description: product?.description || null,
    expiration_date: product?.stock_info?.expiration_date,
    length: product?.stock_info?.length,
    width: product?.stock_info?.width,
    height: product?.stock_info?.height,
    weight: product?.stock_info?.weight,
    barcode: product?.stock_info?.barcode,
    unit: product ? product.unit : '',
    datasheet: product?.datasheet || '',
    metadata: {
      title: product?.meta_data?.title || '',
      description: product?.meta_data?.description || '',
    },
    promelec_params: {
      pack_quant: product?.promelec_params?.pack_quant || 0,
      price_unit: product?.promelec_params?.price_unit || 0,
      munit: product?.promelec_params?.munit || '',
      producer_name: product?.promelec_params?.producer_name || '',
      remark: product?.promelec_params?.remark || '',
      description_eng: product?.promelec_params?.description_eng || '',
      temperature: product?.promelec_params?.temperature || '',
      package: product?.promelec_params?.package || '',
      packaging: product?.promelec_params?.packaging || '',
      weight: product?.promelec_params?.weight || 0,
    },
  };

  if (categories) {
    const id = product?.category?.id || parentCategoryId || catId;
    let tree: ICategoryItem[] = [];
    if (id) {
      categories.forEach(item => {
        const newCategories = findCategory(item, id?.toString(), []);
        if (newCategories && newCategories.length > 0) {
          tree = [item, ...newCategories];
        }
      });
      if (tree.length === 0 && id) {
        initValues.mainCategoryId = id;
      }
      tree.forEach((item, index) => {
        if (index === 0) {
          initValues.mainCategoryId = item.id;
        } else {
          initValues[`categoryId_${index}`] = item.id;
        }
      });
    }
  }

  if (product?.parameter_values) {
    product.parameter_values.forEach(item => {
      const {value} = item;
      initValues[`parameter${item.parameter_id}`] = value;
    });
  }
  return initValues;
};

export const useFormikProductForm = (
  paramLanguage: string,
  fetchTypeId: number | undefined,
  product: IProduct | undefined,
  productCompany: { id?: number; brand?: string } | undefined,
  parentCategoryId: string | undefined,
  typeParams: any,
  images: File[],
  id: string,
  categories?: ICategoryItem[],
  me?: IUser,
  catId?: string
) => {
  const dispatch = useDispatch();
  const fm = useFormatMessage();
  const intl = useIntl();

  const [descEditorState, setDescEditorState] = useState<any>(null);
  const [descEditorStateTranslation, setDescEditorStateTranslation] = useState<any>(null);

  const validationSchema = useMemo(() => {
    const fields: any = {};

    return Yup.object().shape({
      ...fields,
      name: Yup.string().required(fm('PRODUCT.REQUIRED.NAME')).trim(),
      mainCategoryId: Yup.string().required(fm('PRODUCT.REQUIRED.CATEGORY')).trim(),
    });
  }, [typeParams]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitValues(
      'ru',
      product,
      productCompany,
      parentCategoryId,
      categories,
      undefined,
      catId
    ),
    validationSchema,
    onSubmit: submitValues => {
      const data = new FormData();
      submitValues.files.forEach((file: any) => data.append('photo[]', file));
      const metadata = {
        title: submitValues.metadata?.title || '',
        description: submitValues.metadata?.description || '',
      };
      data.append('meta_data', JSON.stringify(metadata));
      data.append('sku', submitValues.sku || '');
      data.append('unit', submitValues.unit || '');
      data.append('name', submitValues.name);
      data.append('datasheet', submitValues.datasheet || '');
      data.append('active', submitValues.active ? 'true' : 'false');
      me?.is_admin && data.append('favorite', submitValues.favorite ? 'true' : 'false');

      if (descEditorState) {
        data.append('description', descEditorState.getData() || '');
      }

      if (submitValues.productTypeId) {
        data.append('product_type_id', String(submitValues.productTypeId));
      }

      if (submitValues.mainCategoryId) {
        data.append('category_id', String(submitValues.mainCategoryId));
      }

      // eslint-disable-next-line no-plusplus
      for (let i = 1; i < 6; i++) {
        if (submitValues[`categoryId_${i}`] && !submitValues[`categoryId_${i + 1}`]) {
          data.append('category_id', String(submitValues[`categoryId_${i}`]));
          break;
        }
      }

      if (id && product) {
        if (
          (product.promelec_params?.pack_quant || 0) !==
            submitValues?.promelec_params?.pack_quant ||
          (product.promelec_params?.price_unit || 0) !==
            submitValues?.promelec_params?.price_unit ||
          (product.promelec_params?.munit || '') !== submitValues?.promelec_params?.munit ||
          (product.promelec_params?.producer_name || '') !==
            submitValues?.promelec_params?.producer_name ||
          (product.promelec_params?.remark || '') !== submitValues?.promelec_params?.remark ||
          (product.promelec_params?.description_eng || '') !==
            submitValues?.promelec_params?.description_eng ||
          (product.promelec_params?.temperature || '') !==
            submitValues?.promelec_params?.temperature ||
          (product.promelec_params?.package || '') !==
            submitValues?.promelec_params?.package ||
          (product.promelec_params?.packaging || '') !==
            submitValues?.promelec_params?.packaging ||
          (product.promelec_params?.weight || 0) !== submitValues?.promelec_params?.weight
        ) {
          const promelecParams = {
            ...submitValues.promelec_params,
          };
          data.append('promelec_params', JSON.stringify(promelecParams));
        }
      }

      if (
        fetchTypeId &&
        submitValues.productTypeId &&
        String(submitValues.productTypeId) === String(fetchTypeId)
      ) {
        typeParams?.forEach((param: { id: number; type: string }) => {
          const { id: paramId, type } = param;
          if (type === 'multiple_enum') {
            if (submitValues[`parameter${paramId}`]) {
              if (submitValues[`parameter${paramId}`].length === 0) {
                data.append(`parameter[${paramId}][]`, 'null');
              }
              submitValues[`parameter${paramId}`].forEach((value: string) => {
                data.append(`parameter[${paramId}][]`, value);
              });
            }
          } else if (
            submitValues[`parameter${paramId}`] &&
            submitValues[`parameter${paramId}`] !== ''
          ) {
            data.append(
              `parameter[${paramId}]`,
              submitValues[`parameter${paramId}`]?.toString()
            );
          } else {
            data.append(`parameter[${paramId}]`, 'null');
          }
        });
      }

      const dataForStock = {
        length: Number(submitValues?.length),
        width: Number(submitValues?.width),
        height: Number(submitValues?.height),
        weight: Number(submitValues?.weight),
        barcode: submitValues?.barcode,
        expirationDateString: submitValues?.expiration_date,
        packageAmount: 0,
        goodsName: '',
      };

      if (product && id && product.id?.toString() === id?.toString()) {
        dispatch(
          productActions.editStockRequest({
            product_id: Number(product.id),
            body: dataForStock,
          })
        );
        dispatch(productActions.editRequest({ id: Number(product.id), data }));
      } else if (!id && !product) {
        submitValues?.length && data.append('length', submitValues.length);
        submitValues?.width && data.append('width', submitValues?.width);
        submitValues?.height && data.append('height', submitValues?.height);
        submitValues?.weight && data.append('weight', submitValues?.weight);
        submitValues?.barcode && data.append('barcode', submitValues?.barcode);
        submitValues?.expiration_date &&
          data.append('expirationDateString', submitValues?.expiration_date);
        images.forEach(img => data.append('photo[]', img));
        dispatch(productActions.addRequest({ data, dataForStock }));
      } else if (!id && product) {
        submitValues?.length && data.append('length', submitValues.length);
        submitValues?.width && data.append('width', submitValues?.width);
        submitValues?.height && data.append('height', submitValues?.height);
        submitValues?.weight && data.append('weight', submitValues?.weight);
        submitValues?.barcode && data.append('barcode', submitValues?.barcode);
        submitValues?.expiration_date &&
          data.append('expirationDateString', submitValues?.expiration_date);
        images.forEach(img => data.append('photo[]', img));
        dispatch(productActions.addRequest({ data, dataForStock }));
      }
    },
  });

  useEffect(() => {
    if (typeParams && typeParams.length > 0) {
      typeParams.forEach((param: ITypeParameter) => {
        if (!formik.values[`parameter${param.id}`]) {
          formik.setFieldValue(
            `parameter${param.id}`,
            param.type === 'multiple_enum' ? [] : ''
          );
        }
      });
    }
  }, [typeParams]);

  return {
    formik,
    setDescEditorState,
    setDescEditorStateTranslation,
    descEditorState,
    descEditorStateTranslation,
  };
};
